import {dyslexiaProfile,readingStylesheets,readingTypography} from './reading-fonts.js';

const styleSets=new WeakMap(),books=new WeakMap();
const base=new URL('./',document.baseURI);
const usedAssets=new Set();
const recordAssets=entries=>{
  const prefix=new URL('fonts/reading/',base);
  for(const entry of entries){
    const url=new URL(entry.name);
    if(url.origin===prefix.origin&&url.pathname.startsWith(prefix.pathname))usedAssets.add(url.href);
  }
};
if('PerformanceObserver'in window){
  const observer=new PerformanceObserver(list=>recordAssets(list.getEntries()));
  observer.observe({type:'resource',buffered:true});
}

function stylesheet(path,doc=document) {
  let styles=styleSets.get(doc);
  if(!styles){styles=new Map();styleSets.set(doc,styles);}
  const url=new URL(path,base).href;
  if(styles.has(url))return styles.get(url);
  const pending=new Promise((resolve,reject)=>{
    const existing=[...doc.querySelectorAll('link[rel="stylesheet"]')].find(link=>link.href===url);
    const link=existing||doc.createElement('link');
    if(link.sheet){resolve();return;}
    link.addEventListener('load',resolve,{once:true});
    link.addEventListener('error',()=>{link.remove();reject(Error(`Reading font stylesheet unavailable: ${path}`));},{once:true});
    if(!existing){link.rel='stylesheet';link.href=url;doc.head.append(link);}
  }).catch(error=>{styles.delete(url);throw error;});
  styles.set(url,pending);
  return pending;
}

async function prepareProfile(profile,doc=document) {
  await Promise.all(readingStylesheets(profile).map(path=>stylesheet(path,doc)));
}

export async function prepareReadingPreviews(language,doc=document) {
  await Promise.all(['literata','inter','dyslexic'].map(family=>prepareProfile(readingTypography(family,language),doc)));
}

function textSamples(book,index,doc=document) {
  const holder=doc.createElement('template');
  const text={normal:book.title||'',bold:book.title||'',italic:'',boldItalic:''};
  for(const chapter of [book.chapters?.[index]].filter(Boolean)){
    text.bold+=' '+chapter.title;
    holder.innerHTML=chapter.html||'';
    text.normal+=' '+holder.content.textContent;
    for(const node of holder.content.querySelectorAll('strong,b,h1,h2,h3,h4,h5,h6'))text.bold+=' '+node.textContent;
    for(const node of holder.content.querySelectorAll('em,i')){
      text.italic+=' '+node.textContent;
      if(node.closest('strong,b')||node.querySelector('strong,b'))text.boldItalic+=' '+node.textContent;
    }
  }
  return text;
}

async function loadSamples(profile,text,doc=document) {
  const mixedArabic=profile.styles.includes('andika')&&/\p{Script=Arabic}/u.test(text.normal);
  if(mixedArabic)await stylesheet('fonts/reading/vazirmatn/index.css',doc);
  const face=profile.family.split(',')[0];
  const families=profile.styles.includes('andika')?[face,'"Reading Andika"']:[face];
  if(mixedArabic||profile.styles.includes('vazirmatn'))families.push('"Reading Vazirmatn"');
  const loads=[];
  for(const name of new Set(families))for(const [style,content]of Object.entries(text)){
    if(!content)continue;
    const spec=`${style==='italic'||style==='boldItalic'?'italic':'normal'} ${style==='bold'||style==='boldItalic'?700:400} 20px ${name}`;
    loads.push(doc.fonts.load(spec,[...new Set(content)].join('')));
  }
  await Promise.all(loads);
}

// Loading faces before changing CSS prevents a late font swap from moving the
// saved anchor. Unicode ranges limit CJK requests to the requested chapters.
export async function prepareReadingFont(family,book,indices=[0]) {
  if(!book||book.kind==='pdf')return;
  const profile=readingTypography(family,book.language);
  if(family==='system')return;
  let cached=books.get(book);
  if(!cached){cached=new Map();books.set(book,cached);}
  let chapters=cached.get(profile);
  if(!chapters){chapters=new Map();cached.set(profile,chapters);}
  await Promise.all([...new Set(indices)].map(index=>{
    if(chapters.has(index))return chapters.get(index);
    const pending=(async()=>{
      await prepareProfile(profile);
      await loadSamples(profile,textSamples(book,index));
    })().catch(error=>{
      chapters.delete(index);
      // Keep native fallback usable when a requested face is unavailable offline.
      console.warn(error);
    });
    chapters.set(index,pending);
    return pending;
  }));
}

export function applyReadingTypography(family,language) {
  const root=document.documentElement,profile=readingTypography(family,language);
  root.style.setProperty('--reader-family',profile.family);
  root.style.setProperty('--reader-letter-spacing',profile.letter);
  root.style.setProperty('--reader-word-spacing',profile.word);
  root.style.setProperty('--reader-word-break',profile.wordBreak||'normal');
  root.style.setProperty('--reader-line-break',profile.lineBreak||'auto');
  root.style.setProperty('--reader-dyslexia-family',dyslexiaProfile(language).family);
  root.style.setProperty('--reader-serif-family',readingTypography('literata',language).family);
  root.style.setProperty('--reader-sans-family',readingTypography('inter',language).family);
}

export function usedReadingAssets() {
  recordAssets(performance.getEntriesByType('resource'));
  return [...usedAssets];
}
